import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sourcingApi, type SourcingPreset } from '@/api/sourcing';

export default function SourcingNewRun() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [presets, setPresets] = useState<SourcingPreset[]>([]);
  const [presetKey, setPresetKey] = useState('');
  const [bucket, setBucket] = useState('A');
  const [inputText, setInputText] = useState('{}');
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    sourcingApi
      .listPresets()
      .then((p) => {
        setPresets(p);
        if (p.length) applyPreset(p[0]);
      })
      .catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (p: SourcingPreset) => {
    setPresetKey(p.key);
    setBucket(p.bucket);
    setInputText(JSON.stringify(p.input, null, 2));
  };

  const parsed = useMemo(() => {
    try {
      return JSON.parse(inputText) as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [inputText]);

  const estCost = useMemo(() => {
    if (!parsed) return null;
    const pages = Number(parsed.takePages) || 0;
    const maxItems = Number(parsed.maxItems) || 0;
    return (0.1 * pages + 0.004 * maxItems).toFixed(2);
  }, [parsed]);

  const start = async () => {
    if (!id || !parsed) return;
    setStarting(true);
    setError(null);
    try {
      const preset = presets.find((p) => p.key === presetKey);
      await sourcingApi.startRun(id, {
        bucket,
        actor_id: preset?.actor_id,
        input_json: parsed,
      });
      navigate(`/sourcing/${id}`);
    } catch (e) {
      setError((e as Error).message);
      setStarting(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <button
        onClick={() => navigate(`/sourcing/${id}`)}
        className="text-sm text-foreground-500 hover:text-foreground-800 cursor-pointer mb-3 flex items-center gap-1"
      >
        <i className="ri-arrow-left-line" /> {t('sourcing.back')}
      </button>

      <h1 className="text-xl font-heading font-bold text-foreground-950 mb-5">
        {t('sourcing.newRun')}
      </h1>

      <div className="bg-white border border-background-200 rounded-2xl px-5 py-4 space-y-4">
        <div>
          <label className="text-xs font-medium text-foreground-500 mb-1.5 block">
            {t('sourcing.preset')}
          </label>
          <div className="flex gap-2 flex-wrap">
            {presets.map((p) => (
              <button
                key={p.key}
                onClick={() => applyPreset(p)}
                title={p.description}
                className={`px-3 py-2 rounded-xl text-xs font-medium cursor-pointer border transition-colors ${
                  presetKey === p.key
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'border-background-200 text-foreground-600 hover:bg-background-100'
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
          {presetKey && (
            <p className="text-xs text-foreground-400 mt-1.5">
              {presets.find((p) => p.key === presetKey)?.description}
            </p>
          )}
        </div>

        <div className="w-32">
          <label className="text-xs font-medium text-foreground-500 mb-1 block">
            {t('sourcing.bucket')}
          </label>
          <select
            value={bucket}
            onChange={(e) => setBucket(e.target.value)}
            className="w-full text-sm border border-background-200 rounded-lg px-2 py-2 cursor-pointer"
          >
            {['A', 'B', 'C', 'D', 'E'].map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-medium text-foreground-500 mb-1 block">
            {t('sourcing.inputJson')}
          </label>
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            rows={18}
            spellCheck={false}
            className={`w-full font-mono text-xs border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 ${
              parsed ? 'border-background-200 focus:ring-primary-400' : 'border-red-300 focus:ring-red-400'
            }`}
          />
          {!parsed && <p className="text-xs text-red-600 mt-1">{t('sourcing.invalidJson')}</p>}
        </div>

        <div className="flex items-center justify-between gap-4 pt-1">
          <div className="text-sm text-foreground-600">
            {estCost != null && (
              <>
                {t('sourcing.estCost')}: <span className="font-mono font-semibold">${estCost}</span>
                <span className="text-xs text-foreground-400 ml-2">({t('sourcing.estCostFormula')})</span>
              </>
            )}
          </div>
          <button
            onClick={start}
            disabled={!parsed || starting}
            className="px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-play-line" />
            {starting ? t('sourcing.starting') : t('sourcing.start')}
          </button>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </div>
  );
}
